import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useChallenge } from "@/contexts/ChallengeContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useReminderProfile } from "@/hooks/useReminderProfile";
import { logAction } from "@/services/eventLogService";
import { todayInIsrael } from "@/lib/dateUtils";
import {
  computeFirstWeeksSteps,
  isFirstWeeksCardVisible,
  shouldTrackShown,
  type FirstWeeksStepId,
} from "@/lib/firstWeeks";
import ReminderTimeSheet from "@/components/reminder/ReminderTimeSheet";

// Per-device state of the card. A second device starts from the defaults.
interface FirstWeeksStored {
  dismissed: boolean;
  /** Israel date on which `first_weeks_card_shown` was last sent. */
  shownDay: string | null;
  /** Israel date on which all four steps were first seen done. */
  allDoneDay: string | null;
}

const DEFAULT_STORED: FirstWeeksStored = { dismissed: false, shownDay: null, allDoneDay: null };

const storageKey = (userId: string) => `sy_first_weeks:${userId}`;

function readStored(userId: string | undefined): FirstWeeksStored {
  if (!userId) return { ...DEFAULT_STORED };
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return { ...DEFAULT_STORED };
    const parsed = JSON.parse(raw) as Partial<FirstWeeksStored> | null;
    return {
      dismissed: parsed?.dismissed === true,
      shownDay: typeof parsed?.shownDay === "string" ? parsed.shownDay : null,
      allDoneDay: typeof parsed?.allDoneDay === "string" ? parsed.allDoneDay : null,
    };
  } catch {
    return { ...DEFAULT_STORED };
  }
}

/** Merge-on-write, so one field never erases another. */
function writeStored(userId: string, patch: Partial<FirstWeeksStored>): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({ ...readStored(userId), ...patch }));
  } catch {
    // Storage unavailable: the in-memory copy still holds for this visit.
  }
}

export default function FirstWeeksCard() {
  const navigate = useNavigate();
  const { effectiveUserId, isImpersonating } = useImpersonation();
  const reminderProfile = useReminderProfile();
  const { progress, currentChallengeId, getCurrentDay } = useChallenge();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Android hardware Back (see useAndroidBackButton): with the reminder sheet
  // open it closes the sheet. Unclaimed, the press would leave Home (or exit
  // the app) with the sheet still up.
  useEffect(() => {
    if (!sheetOpen) return;
    const onHardwareBack = (e: Event) => {
      e.preventDefault();
      setSheetOpen(false);
    };
    window.addEventListener("sy:hardware-back", onHardwareBack);
    return () => window.removeEventListener("sy:hardware-back", onHardwareBack);
  }, [sheetOpen]);

  // Tagged with the user it was read for: the effective user changes when an
  // admin starts or stops viewing as a member, and one user's state must never
  // be shown, or written, as another's.
  const [stored, setStored] = useState(() => ({ userId: effectiveUserId, value: readStored(effectiveUserId) }));
  useEffect(() => {
    setStored((prev) =>
      prev.userId === effectiveUserId ? prev : { userId: effectiveUserId, value: readStored(effectiveUserId) },
    );
  }, [effectiveUserId]);
  const storedReady = stored.userId === effectiveUserId;

  const lessonsCompleted = Object.values(progress?.videosWatched ?? {}).filter((v) => v === true).length;
  const hasReminderTime = !!reminderProfile.data?.preferredVideoTime;

  const { steps, doneCount } = computeFirstWeeksSteps({ lessonsCompleted, hasReminderTime });
  const stepCount = steps.length;

  // No data (first load, or a first load that failed) renders nothing: the
  // card never guesses. Gated on data rather than `isSuccess`, because a
  // failed background refetch flips the status to error while the last good
  // data is kept, and the card (with its open sheet) must not vanish over it.
  const profile = reminderProfile.data;
  const visible =
    !!effectiveUserId &&
    storedReady &&
    profile !== undefined &&
    isFirstWeeksCardVisible({
      createdAt: profile.createdAt,
      now: new Date(),
      dismissed: stored.value.dismissed,
      allDoneDay: stored.value.allDoneDay,
    });

  const { shownDay, allDoneDay } = stored.value;

  useEffect(() => {
    // An impersonating admin leaves no trace: no storage write, no event.
    if (!visible || isImpersonating || !effectiveUserId) return;
    const update = (patch: Partial<FirstWeeksStored>) => {
      writeStored(effectiveUserId, patch);
      setStored((prev) =>
        prev.userId === effectiveUserId ? { userId: effectiveUserId, value: { ...prev.value, ...patch } } : prev,
      );
    };
    // All done: the card stays for the rest of this Israel day, then goes.
    if (doneCount === stepCount && allDoneDay === null) update({ allDoneDay: todayInIsrael() });
    const now = new Date();
    if (shouldTrackShown(shownDay, now)) {
      logAction("feature", "first_weeks_card_shown", { done_count: doneCount });
      update({ shownDay: todayInIsrael(now) });
    }
  }, [visible, isImpersonating, effectiveUserId, doneCount, stepCount, allDoneDay, shownDay]);

  if (!visible) return null;

  const handleDismiss = () => {
    if (isImpersonating || !effectiveUserId) return;
    writeStored(effectiveUserId, { dismissed: true });
    setStored((prev) => ({ userId: effectiveUserId, value: { ...prev.value, dismissed: true } }));
  };

  const handleStepTap = (step: FirstWeeksStepId) => {
    if (isImpersonating) return;
    logAction("feature", "first_weeks_step_tapped", { step });
    if (step === "reminder_time") {
      setSheetOpen(true);
      return;
    }
    // Today's lesson when there is one to open; otherwise (no active challenge,
    // or it has not started yet) the library, never a broken link.
    const day = getCurrentDay();
    if (currentChallengeId && day >= 1) {
      navigate(`/challenge/${currentChallengeId}/day/${day}`);
    } else {
      navigate("/library");
    }
  };

  return (
    <section className="w-full px-0 py-4" dir="rtl">
      <div className="bg-card rounded-2xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground">השבועיים הראשונים שלך</h2>
            <p className="mt-1 text-sm text-muted-foreground">{`${doneCount} מתוך ${stepCount}`}</p>
          </div>
          <button
            type="button"
            aria-label="סגירה"
            onClick={handleDismiss}
            disabled={isImpersonating}
            className="-me-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-muted disabled:opacity-50"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <Progress
          dir="rtl"
          aria-label="התקדמות בשבועיים הראשונים"
          className="mt-3 h-2 w-full"
          value={(doneCount / stepCount) * 100}
        />

        <ul className="mt-3">
          {steps.map((step) => {
            const inactive = step.done || isImpersonating;
            return (
              <li key={step.id}>
                <button
                  type="button"
                  disabled={inactive}
                  onClick={() => handleStepTap(step.id)}
                  className="flex min-h-11 w-full items-center gap-3 rounded-lg py-2 text-start transition-colors enabled:active:bg-muted"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                      step.done ? "bg-success text-success-foreground" : "border-2 border-muted-foreground/40",
                    )}
                  >
                    {step.done && <Check className="h-4 w-4" />}
                  </span>
                  <span className={cn("flex-1 text-base", step.done ? "text-muted-foreground" : "text-foreground")}>
                    {step.label}
                    {step.done && <span className="sr-only"> (הושלם)</span>}
                  </span>
                  {/* "Forward" points left in RTL. */}
                  {!inactive && <ChevronLeft className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Its own invalidation of the reminder profile refreshes the row. */}
      <ReminderTimeSheet open={sheetOpen} onOpenChange={setSheetOpen} source="first_weeks_card" />
    </section>
  );
}
